/**
 * Thin HTTP client.
 * - Adds Bearer token from tokenStore.
 * - Refreshes on 401 once, then retries.
 * - Same JSON contract as BigxBookApp/src/sync HTTP usage.
 *
 * ADAPTED from BigxBookDesktop/src/services/httpClient.ts. The only change is
 * the token backend: Desktop reads/writes `secureStore` (DPAPI) directly, while
 * web routes through `@/services/tokenStore` so the storage strategy is a
 * single swappable module. See tokenStore.ts and BIGXBOOK_WEB_PLAN §7.2.
 *
 * Also adds `credentials: 'include'` so the refresh cookie travels once the
 * backend issues one (tokenStore.COOKIE_MODE); harmless before then.
 */
import { API_BASE_URL } from '@/config';
import { COOKIE_MODE, tokenStore } from './tokenStore';

export class HttpError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
  }
}

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const refresh = tokenStore.getRefreshToken();
      // In cookie mode the browser supplies the token; otherwise no token means
      // no recoverable session.
      if (!refresh && !COOKIE_MODE) return null;
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(refresh ? { refresh_token: refresh } : {}),
      });
      if (!res.ok) {
        // A failed refresh means the session is unrecoverable. Clear it so the
        // UI can route to login rather than retrying with a dead token.
        tokenStore.clear();
        return null;
      }
      const data = (await res.json()) as { access_token?: string; refresh_token?: string };
      tokenStore.setTokens(data.access_token ?? null, data.refresh_token ?? null);
      return data.access_token ?? null;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/**
 * Ensures a usable access token exists, minting one from the refresh token if
 * the in-memory copy is gone (which it always is after a page reload).
 *
 * Exported so startup can populate `useAppStore.authToken` before anything
 * reads it. `sync/index.ts` treats that value as both the "am I authenticated"
 * check and the Bearer token, and returns early — silently — when it is null.
 */
export async function ensureAccessToken(): Promise<string | null> {
  const current = tokenStore.getAccessToken();
  if (current) return current;
  if (!tokenStore.needsRefresh()) return null;
  return refreshAccessToken();
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip auth header (e.g. /auth/request-otp). */
  anonymous?: boolean;
}

export async function http<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers = new Headers(opts.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  if (!opts.anonymous) {
    // After a reload the access token is gone but the session may be
    // recoverable. Mint one up front rather than burning a guaranteed 401.
    let token = tokenStore.getAccessToken();
    if (!token && tokenStore.needsRefresh()) token = await refreshAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const init: RequestInit = {
    ...opts,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  };

  let res = await fetch(url, init);

  if (res.status === 401 && !opts.anonymous) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      headers.set('Authorization', `Bearer ${fresh}`);
      res = await fetch(url, { ...init, headers });
    }
  }

  const text = await res.text();
  const data = text ? safeParse(text) : null;
  if (!res.ok) throw new HttpError(res.status, data);
  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Kept for source compatibility with ported Desktop modules, which import
 * `tokenStorage` from this file. Delegates to tokenStore; the async signatures
 * are preserved so callers keep their `await`s.
 */
export const tokenStorage = {
  async setTokens(access: string, refresh: string) {
    tokenStore.setTokens(access, refresh);
  },
  async clear() {
    tokenStore.clear();
  },
  async hasSession() {
    return tokenStore.hasSession();
  },
  async getAccessToken() {
    return tokenStore.getAccessToken();
  },
  async getRefreshToken() {
    return tokenStore.getRefreshToken();
  },
};
